import { Dexie, Table } from "dexie";
import resolveEan from "pwa/offline/sync/products/resolve-ean";
import {
  MARKETPLACE_CATEGORY_IDENFITIFER_PREFIX,
  MARKETPLACE_SELLER_ID_SERVICEBUND,
  MARKETPLACE_STARTING_PATTERN,
  MARKETPLACE_TOP_CATEGORY_IDENTIFIER,
} from "constants/common";
import { resolveSellerIdentifierForArticle } from "tools/resolve-seller-identifier";
import {
  CategoryWithIndices,
  CustomerProductList,
  NamedNumberValue,
  NamedStringValue,
  OfflineOrderItemLogEntry,
  ProductDetailWithIndices,
  Setting,
  SyncTimestamp,
} from "./types";

export class EFoodDB extends Dexie {
  categories!: Table<CategoryWithIndices, string>;

  products!: Table<ProductDetailWithIndices, string>;

  customerProductLists!: Table<CustomerProductList, string>;

  namedValues!: Table<NamedNumberValue | NamedStringValue, string>;

  syncTimestamps!: Table<SyncTimestamp, string>;

  orderItemLog!: Table<OfflineOrderItemLogEntry, number>;

  settings!: Table<Setting, string>;

  constructor() {
    super("EFoodDB");
    this.version(1).stores({
      categories:
        "uniqueID, identifier, parentCatalogGroupID, *childCatalogGroupIDs",
      products:
        "uniqueID, partNumber, eFoodPartnumber, *parentCatgroupsPath, *customerProductlists, *nameTokens, *shortDescTokens, *longDescTokens, *attrTokens",
      customerProductLists: "id",
      namedValues: "name",
      syncTimestamps: "name",
      orderItemLog: "++pk, orderItemId, partNumber",
      settings: "name",
    });
    this.version(2)
      .stores({
        products:
          "uniqueID, partNumber, eFoodPartnumber, *parentCatgroupsPath, *customerProductlists, *nameTokens, *shortDescTokens, *longDescTokens, *attrTokens, lastUpdate",
      })
      .upgrade(async (tx) => {
        const lastProductViewSync = (
          await tx.table<SyncTimestamp>("syncTimestamps").get("productview")
        )?.value;
        return tx
          .table<ProductDetailWithIndices>("products")
          .toCollection()
          .modify((product) => {
            // eslint-disable-next-line no-param-reassign
            product.lastUpdate = lastProductViewSync;
          });
      });
    this.version(3)
      .stores({
        products:
          "uniqueID, partNumber, eFoodPartnumber, *parentCatgroupsPath, *customerProductlists, *nameTokens, *shortDescTokens, *longDescTokens, *attrTokens, lastUpdate, *ean",
      })
      .upgrade(async (tx) =>
        tx
          .table<ProductDetailWithIndices>("products")
          .toCollection()
          .modify((product) => {
            if (product.ean == null || product.ean.length === 0) {
              Object.assign(product, { ean: resolveEan(product) });
            }

            return product;
          })
      );
    this.version(4)
      .stores({
        products:
          "uniqueID, [sellerId+uniqueID]" +
          ", partNumber, [sellerId+partNumber]" +
          ", *parentCatgroupsPath" +
          ", *customerProductlists" +
          ", *nameTokens" +
          ", *shortDescTokens" +
          ", *longDescTokens" +
          ", *attrTokens" +
          ", lastUpdate" +
          ", *ean" +
          ", sellerId",
      })
      .upgrade(async (tx) =>
        tx
          .table<ProductDetailWithIndices>("products")
          .toCollection()
          .modify((product) => {
            if (product.sellerId == null || product.sellerId.length === 0) {
              Object.assign(product, {
                sellerId: resolveSellerIdentifierForArticle(product),
              });
            }

            return product;
          })
      );
  }

  public async clearSyncTimestamps() {
    return this.syncTimestamps.clear().catch(() => {
      // ignore error
    });
  }

  public async clearOfflineData() {
    const allPromises = [
      this.categories.clear(),
      this.products.clear(),
      this.customerProductLists.clear(),
      this.clearSyncTimestamps(),
      this.orderItemLog.clear(),
    ];

    return Promise.all(allPromises).catch(() => {
      // ignore error
    });
  }

  private async clearInternalSellerOfflineData() {
    const allPromises = [
      this.categories
        .orderBy("identifier")
        .filter(
          (c) =>
            c.identifier !== MARKETPLACE_TOP_CATEGORY_IDENTIFIER &&
            !c.identifier.startsWith(MARKETPLACE_CATEGORY_IDENFITIFER_PREFIX)
        )
        .delete(),
      this.products
        .orderBy("partNumber")
        .filter((p) => !p.partNumber.startsWith(MARKETPLACE_STARTING_PATTERN))
        .delete(),
      this.orderItemLog
        .orderBy("partNumber")
        .filter((p) => !p.partNumber?.startsWith(MARKETPLACE_STARTING_PATTERN))
        .delete(),
    ];

    return Promise.all(allPromises).catch(() => {
      // ignore error
    });
  }

  private async clearExternalSellerOfflineData(sellerId: string) {
    const sellerIdPrefix = `${MARKETPLACE_CATEGORY_IDENFITIFER_PREFIX}${sellerId}-`;
    const allPromises = [
      this.categories
        .where("identifier")
        .equals(`${MARKETPLACE_CATEGORY_IDENFITIFER_PREFIX}${sellerId}`)
        .delete(),
      this.categories.where("identifier").startsWith(sellerIdPrefix).delete(),
      this.products.where("partNumber").startsWith(sellerIdPrefix).delete(),
      this.orderItemLog.where("partNumber").startsWith(sellerIdPrefix).delete(),
    ];

    return Promise.all(allPromises).catch(() => {
      // ignore error
    });
  }

  public async clearOfflineDataOfSeller(sellerId: string) {
    return sellerId === MARKETPLACE_SELLER_ID_SERVICEBUND
      ? this.clearInternalSellerOfflineData()
      : this.clearExternalSellerOfflineData(sellerId);
  }

  public async clearAll() {
    const allPromises = [
      this.clearOfflineData(),
      this.namedValues.clear(),
      this.settings.clear(),
    ];

    return Promise.all(allPromises).catch(() => {
      // ignore error
    });
  }
}

export const eFoodDB = new EFoodDB();

if (typeof document !== "undefined") {
  (window as typeof window & { eFoodDB: EFoodDB }).eFoodDB = eFoodDB;
}
